'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { CalendarDate, DateValue, now, today, toZoned } from '@internationalized/date'
import { Button, Select, SelectItem, SelectSection } from '@nextui-org/react'
import { I18nProvider } from '@react-aria/i18n'
import { format } from 'date-fns'
import { useLocale, useTranslations } from 'next-intl'
import { union } from 'ramda'
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useEffectOnce } from 'react-use'
import { z } from 'zod'

import { useDestination } from '@/app/[locale]/destination/context/DestinationContext'
import { MaintenanceModal, useMaintenance } from '@/components/ui/maintenance'
import { env } from '@/constants/configs'
import { fetchCommon } from '@/libs/actions/common.action'
import { cookies } from '@/libs/cookie'
import { generateAvailableDatetime } from '@/libs/date'
import { geoBeanchLocation } from '@/libs/haversine'
import { locales } from '@/libs/intl'

import { DatePicker } from '../ui/datepicker'

const selectClassNames = {
  trigger: 'shadow-none rounded bg-theme-200/20 hover:!bg-theme-200/25',
  label: 'text-theme/90',
  value: 'font-bold',
  popoverContent: 'rounded'
}
const selectItemClassNames = 'rounded data-[selected=true]:!bg-theme-200/60'

const formSchema = z.object({
  cs_location: z.string(),
  cs_appdate: z.string(),
  cs_apptime: z.string(),
  cs_guest: z.string(),
  utm_source: z.string().optional(),
  utm_content: z.string().optional(),
  csrf: z.string().optional()
})

type Form = z.infer<typeof formSchema>

interface BookingFormComponentProps {
  defaultBranchId?: string
  allowChange?: boolean
}

export function BookingFormComponent({
  defaultBranchId,
  allowChange = true
}: BookingFormComponentProps) {
  // __STATE's
  const formRef = useRef<HTMLFormElement>(null)
  const form = useForm<Partial<Form>>({ resolver: zodResolver(formSchema) })

  const locale = useLocale()
  const t = useTranslations()
  const maintenance = useMaintenance()

  // Branch list is SSR'd by the layout into DestinationProvider — read it instead of refetching.
  const { data: destination } = useDestination()
  const source = cookies.get('source')
  const utmContent = cookies.get('utm_content')

  const currentLocale = useMemo(() => locales.find((r) => r.key === locale), [locale])

  const [initialBranchSet, setInitialBranchSet] = useState(false)
  const [branches, setBranches] = useState<IBranch[]>([])
  const [currentBranch, setCurrentBranch] = useState<Branch>()
  const [intervals, setIntervals] = useState(() => {
    // If we have a defaultBranchId, wait for initializeBranch to set proper times
    if (defaultBranchId) {
      return generateAvailableDatetime(now(env.TZ), '10:00', '22:00', env.TZ, false)
    }

    // For homepage (no defaultBranchId), apply 2-hour rule if today
    const isToday = now(env.TZ).compare(today(env.TZ)) === 0
    return generateAvailableDatetime(now(env.TZ), '10:00', '22:00', env.TZ, isToday)
  })
  const [currentActionUrl, setCurrentActionUrl] = useState(
    `${env.APP_BASE_URL}/${currentLocale?.key}/onlinebooking/`
  )

  // __FUNCTION's
  const onSubmit = async (data: Partial<Form>) => {
    if (await maintenance.check()) return

    formRef.current?.submit()
  }
  const current = now(env.TZ)
  const minDate = current.hour < 21 ? current : current.add({ days: 1 })

  const handleBranchChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const branchId = e.target.value
      const branch = branches
        ?.map((r) => r.branches)
        .reduce<Branch[]>(union, [])
        .find((r) => r.id == branchId)

      if (branch) {
        setCurrentBranch(branch)

        const [open, close] = [branch.open_timename, branch.close_timename]

        // Check if user has already selected a date
        const currentSelectedDate = form.watch('cs_appdate')

        if (currentSelectedDate) {
          // Parse the selected date and preserve it
          const year = parseInt(currentSelectedDate.substring(0, 4))
          const month = parseInt(currentSelectedDate.substring(4, 6))
          const day = parseInt(currentSelectedDate.substring(6, 8))
          const selectedCalendarDate = new CalendarDate(year, month, day)
          const selectedZonedDate = toZoned(selectedCalendarDate, env.TZ)

          // Check if selected date is today to apply 2-hour rule
          const isToday = selectedZonedDate.compare(today(env.TZ)) === 0

          // Generate intervals for the existing selected date with new branch hours
          const intervals = generateAvailableDatetime(
            selectedZonedDate,
            open,
            close,
            env.TZ,
            isToday
          )
          setIntervals(intervals)

          // Only clear selected time if it is not available in new intervals
          const selectedTime = form.watch('cs_apptime')
          const availableTimes = intervals.map(
            (record) =>
              `${String(record.hour).padStart(2, '0')}:${String(record.minute).padStart(2, '0')}`
          )
          if (!selectedTime || !availableTimes.includes(selectedTime)) {
            form.resetField('cs_apptime')
          }
        } else {
          // No date selected yet, check if current date is today for 2-hour rule
          const currentTime = now(env.TZ)
          const isToday = currentTime.compare(today(env.TZ)) === 0
          const intervals = generateAvailableDatetime(currentTime, open, close, env.TZ, isToday)
          setIntervals(intervals)
          form.resetField('cs_apptime')
        }

        if (branch?.cross_http_origin) {
          setCurrentActionUrl(`${branch.cross_http_origin}/${currentLocale?.key}/onlinebooking/`)
        } else {
          setCurrentActionUrl(`${env.APP_BASE_URL}/${currentLocale?.key}/onlinebooking/`)
        }
      }

      form.setValue('cs_location', branchId)
    },
    [branches, currentLocale, form]
  )

  const handleDateChange = useCallback(
    (value: DateValue | undefined) => {
      let open = '10:00'
      let close = '22:00'

      if (currentBranch) {
        ;[open, close] = [currentBranch.open_timename, currentBranch.close_timename]
      }

      // The picker's reset button passes `undefined`: clear both fields and fall back to
      // today's slot list so the time Select is not left holding a stale date's intervals.
      if (!value) {
        form.resetField('cs_appdate')
        form.resetField('cs_apptime')
        const isToday = now(env.TZ).compare(today(env.TZ)) === 0
        setIntervals(generateAvailableDatetime(now(env.TZ), open, close, env.TZ, isToday))
        return
      }

      const appdate = toZoned(value, env.TZ)
      const isToday = appdate.compare(today(env.TZ)) === 0

      const intervals = generateAvailableDatetime(appdate, open, close, env.TZ, isToday)
      setIntervals(intervals)

      // Only clear selected time if it is not available in new intervals
      const selectedTime = form.watch('cs_apptime')
      const availableTimes = intervals.map(
        (record) =>
          `${String(record.hour).padStart(2, '0')}:${String(record.minute).padStart(2, '0')}`
      )
      if (!selectedTime || !availableTimes.includes(selectedTime)) {
        form.resetField('cs_apptime')
      }
      form.setValue('cs_appdate', format(appdate.toDate(), 'yyyyMMdd'))
    },
    [currentBranch, form]
  )

  const initializeBranch = useCallback(
    (branchList: IBranch[]) => {
      if (defaultBranchId && !initialBranchSet) {
        const allBranches = branchList.map((r) => r.branches).reduce<Branch[]>(union, [])
        const branch = allBranches.find((b) => b.id === defaultBranchId)

        if (branch) {
          setCurrentBranch(branch)
          form.setValue('cs_location', branch.id)

          const [open, close] = [branch.open_timename, branch.close_timename]
          // Check if current date is today to apply 2-hour rule correctly
          const isToday = now(env.TZ).compare(today(env.TZ)) === 0
          const intervals = generateAvailableDatetime(now(env.TZ), open, close, env.TZ, isToday)
          setIntervals(intervals)

          if (branch?.cross_http_origin) {
            setCurrentActionUrl(`${branch.cross_http_origin}/${currentLocale?.key}/onlinebooking/`)
          }
          setInitialBranchSet(true)
        }
      }
    },
    [defaultBranchId, currentLocale, form, initialBranchSet]
  )

  // __EFFECT's
  useEffectOnce(() => {
    // Branch list is already in hand from the layout's SSR — apply it before anything awaits, so
    // the select is populated on first paint instead of after a round trip.
    const branches: IBranch[] | undefined = destination?.branches
    if (branches?.length) {
      setBranches(branches)
      initializeBranch(branches)
    }

    async function fetchCsrf() {
      // Still fetched: the CSRF token must be fresh at submit time, a cached page's is not.
      const common = await fetchCommon(locale)
      if (common) form.setValue('csrf', common.CSRF_TOKEN)
    }

    async function selectNearest() {
      if (!branches?.length || defaultBranchId) return

      try {
        const nearestBranch = await geoBeanchLocation(
          branches.map((r) => r.branches).reduce<Branch[]>(union, [])
        )
        if (nearestBranch) {
          // Copy first — `branches` is the context's array now, sorting in place mutates it.
          const branchSorted = [...branches].sort((a) =>
            a.code === nearestBranch.city_code ? -1 : 1
          )
          setBranches(branchSorted)

          // Auto-select the nearest branch and update intervals
          setCurrentBranch(nearestBranch)
          form.setValue('cs_location', nearestBranch.id)

          const [open, close] = [nearestBranch.open_timename, nearestBranch.close_timename]
          const isToday = now(env.TZ).compare(today(env.TZ)) === 0
          const intervals = generateAvailableDatetime(now(env.TZ), open, close, env.TZ, isToday)
          setIntervals(intervals)

          if (nearestBranch?.cross_http_origin) {
            setCurrentActionUrl(
              `${nearestBranch.cross_http_origin}/${currentLocale?.key}/onlinebooking/`
            )
          }
        }
      } catch (error) {
        // Keep original branches order if geolocation fails
      }
    }

    fetchCsrf()
    selectNearest()
    form.setValue('utm_source', source)
    form.setValue('utm_content', utmContent)
  })

  // __RENDER
  return (
    <>
      <form
        className='flex min-w-72 flex-col gap-8 border-b-2 border-gray-300 pb-4 md:flex-row md:gap-2 md:py-4'
        method='post'
        action={currentActionUrl}
        onSubmit={form.handleSubmit(onSubmit)}
        ref={formRef}>
        <input type='hidden' {...form.register('csrf')} />
        <input type='hidden' {...form.register('cs_location')} />
        <input type='hidden' {...form.register('cs_appdate')} />
        <input type='hidden' {...form.register('cs_apptime')} />
        <input type='hidden' {...form.register('cs_guest')} />
        <input type='hidden' {...form.register('utm_source')} />
        <input type='hidden' {...form.register('utm_content')} />

        <Select
          classNames={selectClassNames}
          key='PLACEHOLDER_LOCATION'
          variant='flat'
          size='sm'
          radius='none'
          label={t('Menu.PLACEHOLDER_LOCATION')}
          labelPlacement='inside'
          isRequired
          isDisabled={!allowChange && defaultBranchId !== undefined}
          scrollShadowProps={{ isEnabled: false }}
          errorMessage={form.formState.errors?.cs_location?.message}
          onChange={handleBranchChange}
          selectedKeys={(() => {
            const selectedBranch = form.watch('cs_location')
            if (selectedBranch) return [selectedBranch]
            if (defaultBranchId && !initialBranchSet) return [defaultBranchId]
            return []
          })()}
          defaultSelectedKeys={defaultBranchId ? [defaultBranchId] : []}>
          {branches?.map((record) => (
            <SelectSection
              classNames={{ heading: 'text-sm font-bold text-theme' }}
              title={record.name}
              key={record.code}>
              {record.branches.map((branch) => (
                <SelectItem className={selectItemClassNames} value={branch.id} key={branch.id}>
                  {branch.name}
                </SelectItem>
              ))}
            </SelectSection>
          ))}
        </Select>

        <I18nProvider locale={currentLocale?.locale}>
          <DatePicker
            lang={currentLocale?.locale}
            key='PLACEHOLDER_DATE'
            label={t('Menu.PLACEHOLDER_DATE')}
            value={form.watch('cs_appdate')}
            minDate={minDate}
            maxDate={today(env.TZ).add({ years: 1 })}
            errorMessage={form.formState.errors?.cs_appdate?.message}
            onChange={handleDateChange}
          />
        </I18nProvider>

        <Select
          classNames={selectClassNames}
          key={`PLACEHOLDER_TIME_${currentBranch?.id || 'default'}_${form.watch('cs_appdate') || 'today'}_${intervals.length}`}
          variant='flat'
          size='sm'
          radius='none'
          label={t('Menu.PLACEHOLDER_TIME')}
          labelPlacement='inside'
          selectedKeys={(() => {
            const selectedTime = form.watch('cs_apptime')
            return selectedTime ? [selectedTime] : []
          })()}
          isRequired
          scrollShadowProps={{ isEnabled: false }}
          errorMessage={form.formState.errors?.cs_apptime?.message}
          onChange={({ target }) => form.setValue('cs_apptime', target.value)}>
          {intervals.map((record, index) => {
            const hours = String(record.hour).padStart(2, '0')
            const minutes = String(record.minute).padStart(2, '0')
            const value = `${hours}:${minutes}`

            return (
              <SelectItem className={selectItemClassNames} value={value} key={value}>
                {value}
              </SelectItem>
            )
          })}
        </Select>

        <Select
          classNames={selectClassNames}
          key='PLACEHOLDER_GUEST_BG'
          variant='flat'
          size='sm'
          radius='none'
          label={t('Menu.PLACEHOLDER_GUEST_BG')}
          labelPlacement='inside'
          isRequired
          scrollShadowProps={{ isEnabled: false }}
          errorMessage={form.formState.errors?.cs_guest?.message}
          onChange={({ target }) => form.setValue('cs_guest', target.value)}>
          {Array.from({ length: 10 }).map((_, index) => (
            <SelectItem
              className={selectItemClassNames}
              value={Number(index) + 1}
              key={Number(index) + 1}>
              {String(index + 1)}
            </SelectItem>
          ))}
        </Select>

        <Button
          className='rounded bg-theme-orange py-4 text-sm font-semibold text-white shadow-sm'
          type='submit'
          size='lg'
          radius='none'
          data-track='booking-submit-form'
          aria-label='Button submit booking'>
          {t('Menu.BOOKNOW_BUTTON')}
        </Button>
      </form>

      <MaintenanceModal
        isOpen={maintenance.isBlocked}
        message={maintenance.message}
        onClose={maintenance.close}
      />
    </>
  )
}
